import {createContext, Dispatch, SetStateAction, useEffect, useState} from 'react'
import qs from 'qs'
import {ID, QueryResponseContextProps, QueryState} from './models'

function createResponseContext<T>(initialState: QueryResponseContextProps<T>) {
  return createContext(initialState)
}

function isNotEmpty(obj: unknown) {
  return obj !== undefined && obj !== null && obj !== ''
}

// Example: page=1&items_per_page=10&sort=id&order=desc&search=a&filter_name=a&filter_online=false
function stringifyRequestQuery(state: QueryState): string {
  const pagination = qs.stringify(state, {filter: ['page_number', 'page_size'], skipNulls: true})
  const sort = qs.stringify(state, {filter: ['sort_field', 'sort_order'], skipNulls: true})
  const search = isNotEmpty(state.search)
    ? qs.stringify(state, {filter: ['search'], skipNulls: true})
    : ''

  const filter = state.filter ? `filter=${JSON.stringify(state.filter)}` : ''

  return [pagination, sort, search, filter]
    .filter((f) => f)
    .join('&')
    .toLowerCase()
}

function parseRequestQuery(query: string): QueryState {
  const cache: unknown = qs.parse(query)
  return cache as QueryState
}

function calculatedGroupingIsDisabled<T>(isLoading: boolean, data: Array<T> | undefined): boolean {
  if (isLoading) {
    return true
  }

  return !data || !data.length
}

function calculateIsAllDataSelected<T>(data: Array<T> | undefined, selected: Array<ID>): boolean {
  if (!data) {
    return false
  }

  return data.length > 0 && data.length === selected.length
}
function calculateIsAllDataSelected2<T>(
  data: Array<T> | undefined,
  selected: {id: ID; val: string}[]
): boolean {
  if (!data) {
    return false
  }

  return data.length > 0 && data.length === selected.length
}

function groupingOnSelect(
  id: ID,
  selected: Array<ID>,
  setSelected: Dispatch<SetStateAction<Array<ID>>>
) {
  if (!id) {
    return
  }

  if (selected.includes(id)) {
    setSelected(selected.filter((itemId) => itemId !== id))
  } else {
    const updatedSelected = [...selected]
    updatedSelected.push(id)
    setSelected(updatedSelected)
  }
}
function groupingOnSelect2(
  id: ID,
  selected2: {id: ID; val: string}[],
  setSelected2: Dispatch<SetStateAction<{id: ID; val: string}[]>>,
  val: string
) {
  if (!id) {
    return
  }

  if (selected2.some((s) => s.id === id)) {
    setSelected2(
      selected2.map((item) => {
        if (item.id === id) item.val = val
        return item
      })
    )
  } else {
    const updatedSelected = [...selected2]
    updatedSelected.push({id, val})
    setSelected2(updatedSelected)
  }
}

function groupingOnSelectAll<T>(
  isAllSelected: boolean,
  setSelected: Dispatch<SetStateAction<Array<ID>>>,
  data?: Array<T & {id?: ID}>
) {
  if (isAllSelected) {
    setSelected([])
    return
  }

  if (!data || !data.length) {
    return
  }

  setSelected(data.filter((item) => item.id).map((item) => item.id))
}

function groupingOnSelectAll2<T>(
  isAllSelected: boolean,
  setSelected2: Dispatch<SetStateAction<{id: ID; val: string}[]>>,
  val: string,
  data?: Array<T & {id?: ID}>
) {
  if (isAllSelected) {
    setSelected2([])
    return
  }

  if (!data || !data.length) {
    return
  }

  setSelected2(
    data
      .filter((item) => item.id)
      .map((item) => {
        return {id: item.id, val}
      })
  )
}

// Hook
function useDebounce(value: string | undefined, delay: number) {
  // State and setters for debounced value
  const [debouncedValue, setDebouncedValue] = useState(value)
  useEffect(
    () => {
      // Update debounced value after delay
      const handler = setTimeout(() => {
        setDebouncedValue(value)
      }, delay)
      // Cancel the timeout if value changes (also on delay change or unmount)
      // This is how we prevent debounced value from updating if value is changed ...
      // .. within the delay period. Timeout gets cleared and restarted.
      return () => {
        clearTimeout(handler)
      }
    },
    [value, delay] // Only re-call effect if value or delay changes
  )
  return debouncedValue
}

export {
  createResponseContext,
  stringifyRequestQuery,
  parseRequestQuery,
  calculatedGroupingIsDisabled,
  calculateIsAllDataSelected,
  calculateIsAllDataSelected2,
  groupingOnSelect,
  groupingOnSelect2,
  groupingOnSelectAll,
  groupingOnSelectAll2,
  useDebounce,
  isNotEmpty,
}
